<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Record Health Education Session</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Session Type Selection -->
          <div class="mb-4">
            <label class="form-label">Session Type <span class="text-danger">*</span></label>
            <div class="row g-3">
              <div class="col-md-4">
                <div class="form-check card p-3"
                     :class="{'border-primary bg-light': educationForm.sessionType === 'individual'}">
                  <input class="form-check-input" type="radio" name="sessionType"
                         id="individualSession" value="individual" v-model="educationForm.sessionType">
                  <label class="form-check-label ms-2" for="individualSession">
                    <i class="bx bx-user"></i> Individual
                    <br>
                    <small class="text-muted">One-on-one counseling</small>
                  </label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check card p-3"
                     :class="{'border-primary bg-light': educationForm.sessionType === 'group'}">
                  <input class="form-check-input" type="radio" name="sessionType"
                         id="groupSession" value="group" v-model="educationForm.sessionType">
                  <label class="form-check-label ms-2" for="groupSession">
                    <i class="bx bx-group"></i> Group
                    <br>
                    <small class="text-muted">Small group session</small>
                  </label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check card p-3"
                     :class="{'border-primary bg-light': educationForm.sessionType === 'community'}">
                  <input class="form-check-input" type="radio" name="sessionType"
                         id="communitySession" value="community" v-model="educationForm.sessionType">
                  <label class="form-check-label ms-2" for="communitySession">
                    <i class="bx bx-buildings"></i> Community
                    <br>
                    <small class="text-muted">Community gathering</small>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Household Selection (for individual sessions) -->
          <div v-if="educationForm.sessionType === 'individual'" class="mb-4">
            <label class="form-label">Select Household.</label>
            <div class="search-box mb-2">
              <div class="position-relative">
                <input v-model="householdSearch" @input="searchHouseholds" type="text"
                       class="form-control" placeholder="Search household number or head name...">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
            <div v-if="selectedHousehold" class="alert alert-info py-2">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="mb-1">{{ selectedHousehold.household_number }}</h6>
                  <p class="mb-1">
                    <strong>Head:</strong> {{ selectedHousehold.household_head_name || 'Not assigned' }}
                    <span v-if="selectedHousehold.head_phone" class="ms-2">
                      <i class="bx bx-phone"></i> {{ selectedHousehold.head_phone }}
                    </span>
                  </p>
                  <small class="text-muted">
                    <i class="bx bx-map"></i> {{ selectedHousehold.kebele_name }}, {{ selectedHousehold.woreda_name }}
                    <span class="ms-2">
                      <i class="bx bx-group"></i> {{ selectedHousehold.member_count || 0 }} members
                    </span>
                  </small>
                </div>
                <button @click="selectedHousehold = null; householdSearch = ''" class="btn btn-sm btn-light">
                  <i class="bx bx-x"></i> Change
                </button>
              </div>
            </div>
            <div v-if="householdResults.length > 0 && !selectedHousehold" class="list-group">
              <button v-for="household in householdResults" :key="household.household_id"
                      @click="selectHousehold(household)" type="button"
                      class="list-group-item list-group-item-action">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 class="mb-1">{{ household.household_number }}</h6>
                    <p class="mb-1 text-muted">
                      <i class="bx bx-user"></i> {{ household.household_head_name || 'No head assigned' }}
                      <span v-if="household.head_phone" class="ms-2">
                        <i class="bx bx-phone"></i> {{ household.head_phone }}
                      </span>
                    </p>
                    <small class="text-muted">
                      <i class="bx bx-map"></i> {{ household.kebele_name }}
                      <span class="ms-2">
                        <i class="bx bx-group"></i> {{ household.member_count || 0 }} members
                      </span>
                      <span v-if="household.cbhi_member_count > 0" class="ms-2 text-success">
                        <i class="bx bx-check-circle"></i> {{ household.cbhi_member_count }} CBHI
                      </span>
                    </small>
                  </div>
                  <div class="text-end">
                    <small class="text-muted">
                      <span v-if="household.is_model_household" class="badge bg-warning me-1">
                        <i class="bx bx-star"></i> Model
                      </span>
                      <span v-if="household.is_vulnerable" class="badge bg-danger">
                        <i class="bx bx-shield-x"></i> Vulnerable
                      </span>
                    </small>
                  </div>
                </div>
              </button>
            </div>
            <div v-else-if="householdSearch.length >= 2 && householdResults.length === 0 && !selectedHousehold && !loading"
                 class="alert alert-warning py-2">
              <small>No households found matching "{{ householdSearch }}"</small>
            </div>
          </div>

          <!-- Topic Selection -->
          <div class="mb-4">
            <label class="form-label">Education Topic <span class="text-danger">*</span></label>
            <select v-model="educationForm.topicCategory" @change="updateTopicList"
                    class="form-select mb-3" required>
              <option value="">Select category...</option>
              <option value="maternal_health">Maternal & Child Health</option>
              <option value="nutrition">Nutrition</option>
              <option value="hygiene">Hygiene & Sanitation</option>
              <option value="disease_prevention">Disease Prevention</option>
              <option value="family_planning">Family Planning</option>
              <option value="immunization">Immunization</option>
              <option value="environmental">Environmental Health</option>
              <option value="other">Other</option>
            </select>

            <select v-if="educationForm.topicCategory" v-model="educationForm.topic"
                    class="form-select" required>
              <option value="">Select specific topic...</option>
              <option v-for="topic in getTopicsForCategory()" :key="topic" :value="topic">
                {{ topic }}
              </option>
            </select>

            <input v-if="educationForm.topic === 'Other'" v-model="educationForm.customTopic"
                   type="text" class="form-control mt-2" placeholder="Specify topic...">
          </div>

          <!-- Session Details -->
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Date & Time <span class="text-danger">*</span></label>
              <input v-model="educationForm.sessionDate" type="datetime-local"
                     class="form-control" :max="currentDateTime" required>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
              <input v-model="educationForm.durationMinutes" type="number"
                     class="form-control" min="5" max="240" required>
            </div>
          </div>

          <!-- Venue -->
          <div class="mb-3">
            <label class="form-label">Venue <span class="text-danger">*</span></label>
            <select v-model="educationForm.venue" class="form-select" required>
              <option value="">Select venue...</option>
              <option value="health_post">Health Post</option>
              <option value="household">Household</option>
              <option value="school">School</option>
              <option value="community_center">Community Center</option>
              <option value="religious_place">Religious Place</option>
              <option value="market">Market Area</option>
              <option value="other">Other</option>
            </select>
            <input v-if="educationForm.venue === 'other'" v-model="educationForm.customVenue"
                   type="text" class="form-control mt-2" placeholder="Specify venue...">
          </div>

          <!-- Target Audience -->
          <div class="mb-3">
            <label class="form-label">Target Audience <span class="text-danger">*</span></label>
            <select v-model="educationForm.targetAudience" class="form-select" required>
              <option value="">Select audience...</option>
              <option value="pregnant_women">Pregnant Women</option>
              <option value="mothers">Mothers with Children</option>
              <option value="adolescents">Adolescents</option>
              <option value="men">Men</option>
              <option value="elderly">Elderly</option>
              <option value="mixed">Mixed Group</option>
              <option value="health_volunteers">Health Development Army</option>
            </select>
          </div>

          <!-- Attendance -->
          <div v-if="educationForm.sessionType !== 'individual'" class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label">Total Attendees <span class="text-danger">*</span></label>
              <input v-model="educationForm.attendeesCount" @input="updateAttendeeBreakdown"
                     type="number" class="form-control" min="1" required>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">Male</label>
              <input v-model="educationForm.maleAttendees" type="number"
                     class="form-control" min="0" :max="educationForm.attendeesCount">
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">Female</label>
              <input v-model="educationForm.femaleAttendees" type="number"
                     class="form-control" min="0" :max="educationForm.attendeesCount">
            </div>
          </div>

          <!-- Key Messages -->
          <div class="mb-3">
            <label class="form-label">Key Messages Delivered <span class="text-danger">*</span></label>
            <div class="mb-2">
              <div v-for="(message, index) in educationForm.keyMessages" :key="index"
                   class="input-group mb-2">
                <input v-model="educationForm.keyMessages[index]" type="text"
                       class="form-control" placeholder="Enter key message...">
                <button @click="removeMessage(index)" class="btn btn-outline-danger"
                        type="button" v-if="educationForm.keyMessages.length > 1">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </div>
            <button @click="addMessage" type="button" class="btn btn-sm btn-outline-primary">
              <i class="bx bx-plus"></i> Add Message
            </button>
          </div>

          <!-- Materials Used -->
          <div class="mb-3">
            <label class="form-label">Educational Materials Used</label>
            <div class="row g-2">
              <div v-for="material in availableMaterials" :key="material.value" class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox"
                         :id="'material_' + material.value" :value="material.value"
                         v-model="educationForm.materialsUsed">
                  <label class="form-check-label" :for="'material_' + material.value">
                    {{ material.label }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Demonstration -->
          <div class="mb-3">
            <div class="form-check">
              <input v-model="educationForm.demonstrationDone" class="form-check-input"
                     type="checkbox" id="demonstrationCheck">
              <label class="form-check-label" for="demonstrationCheck">
                Practical demonstration conducted
              </label>
            </div>
            <input v-if="educationForm.demonstrationDone" v-model="educationForm.demonstrationDetails"
                   type="text" class="form-control mt-2"
                   placeholder="Describe the demonstration...">
          </div>

          <!-- Notes -->
          <div class="mb-3">
            <label class="form-label">Additional Notes</label>
            <textarea v-model="educationForm.notes" class="form-control" rows="2"
                      placeholder="Any additional observations or follow-up needed..."></textarea>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-success" @click="recordEducation"
                  :disabled="!isFormValid || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Recording...
            </span>
            <span v-else>
              <i class="bx bx-check"></i> Record Session
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "RecordEducationModal",
  data() {
    return {
      loading: false,
      submitting: false,

      // Form data
      educationForm: {
        sessionType: 'individual',
        sessionDate: '',
        topicCategory: '',
        topic: '',
        customTopic: '',
        householdId: null,
        targetAudience: '',
        attendeesCount: 1,
        maleAttendees: 0,
        femaleAttendees: 1,
        venue: '',
        customVenue: '',
        keyMessages: [''],
        materialsUsed: [],
        demonstrationDone: false,
        demonstrationDetails: '',
        durationMinutes: 30,
        notes: ''
      },

      // Household search
      householdSearch: '',
      householdResults: [],
      selectedHousehold: null,

      // Reference data
      availableMaterials: [
        { value: 'posters', label: 'Posters/Charts' },
        { value: 'flipchart', label: 'Flip Chart' },
        { value: 'brochures', label: 'Brochures/Leaflets' },
        { value: 'video', label: 'Video/Audio' },
        { value: 'demonstration_kit', label: 'Demonstration Kit' },
        { value: 'samples', label: 'Product Samples' }
      ],

      topicsByCategory: {
        maternal_health: [
          'Antenatal Care Importance',
          'Danger Signs in Pregnancy',
          'Birth Preparedness',
          'Postnatal Care',
          'Newborn Care',
          'Breastfeeding',
          'Other'
        ],
        nutrition: [
          'Balanced Diet',
          'Child Feeding Practices',
          'Malnutrition Prevention',
          'Food Preparation & Safety',
          'Kitchen Gardening',
          'Other'
        ],
        hygiene: [
          'Hand Washing',
          'Personal Hygiene',
          'Household Cleanliness',
          'Water Treatment',
          'Latrine Use & Maintenance',
          'Other'
        ],
        disease_prevention: [
          'Malaria Prevention',
          'Diarrhea Prevention',
          'COVID-19 Prevention',
          'TB Prevention',
          'HIV/AIDS Awareness',
          'Other'
        ],
        family_planning: [
          'Family Planning Methods',
          'Birth Spacing',
          'Contraceptive Options',
          'Myths and Misconceptions',
          'Other'
        ],
        immunization: [
          'Importance of Vaccination',
          'Vaccine Schedule',
          'Vaccine Safety',
          'Catch-up Vaccination',
          'Other'
        ],
        environmental: [
          'Waste Management',
          'Vector Control',
          'Safe Water Storage',
          'Home Environment',
          'Other'
        ]
      },

      currentDateTime: new Date().toISOString().slice(0, 16)
    };
  },

  computed: {
    isFormValid() {
      return this.educationForm.sessionType &&
          this.educationForm.sessionDate &&
          this.educationForm.topicCategory &&
          this.educationForm.topic &&
          (this.educationForm.topic !== 'Other' || this.educationForm.customTopic) &&
          this.educationForm.targetAudience &&
          this.educationForm.venue &&
          (this.educationForm.venue !== 'other' || this.educationForm.customVenue) &&
          this.educationForm.durationMinutes > 0 &&
          this.educationForm.keyMessages.some(m => m.trim() !== '') &&
          (this.educationForm.sessionType !== 'individual' || this.selectedHousehold);
    }
  },

  mounted() {
    // Set default date to now
    this.educationForm.sessionDate = new Date().toISOString().slice(0, 16);
  },

  methods: {
    async searchHouseholds() {
      if (!this.householdSearch || this.householdSearch.length < 2) {
        this.householdResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "getHouseholds",
        "searchTerm": this.householdSearch.trim(),
        "limit": 5,
        "offset": 0
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.householdResults = res.data.data.households || [];

          // If no results found, show a message
          if (this.householdResults.length === 0) {
            console.log("No households found for search term:", this.householdSearch);
          }
        } else {
          console.error("Failed to search households:", res.data.message);
          this.householdResults = [];
        }
      } catch (e) {
        console.error("Failed to search households:", e);
        this.householdResults = [];
      } finally {
        this.loading = false;
      }
    },

    selectHousehold(household) {
      this.selectedHousehold = household;
      this.educationForm.householdId = household.household_id;
      this.householdResults = [];
      this.householdSearch = '';
    },

    getTopicsForCategory() {
      return this.topicsByCategory[this.educationForm.topicCategory] || [];
    },

    updateTopicList() {
      this.educationForm.topic = '';
      this.educationForm.customTopic = '';
    },

    updateAttendeeBreakdown() {
      const total = parseInt(this.educationForm.attendeesCount) || 0;
      const male = parseInt(this.educationForm.maleAttendees) || 0;
      const female = parseInt(this.educationForm.femaleAttendees) || 0;

      if (male + female > total) {
        this.educationForm.femaleAttendees = total - male;
      }
    },

    addMessage() {
      this.educationForm.keyMessages.push('');
    },

    removeMessage(index) {
      this.educationForm.keyMessages.splice(index, 1);
    },

    async recordEducation() {
      if (!this.isFormValid) return;

      this.submitting = true;

      // Prepare data
      const topic = this.educationForm.topic === 'Other'
          ? this.educationForm.customTopic
          : this.educationForm.topic;

      const venue = this.educationForm.venue === 'other'
          ? this.educationForm.customVenue
          : this.educationForm.venue;

      const keyMessages = this.educationForm.keyMessages
          .filter(m => m.trim() !== '')
          .join('; ');

      const data = {
        "function": "recordHealthEducation",
        "sessionDate": this.educationForm.sessionDate,
        "sessionType": this.educationForm.sessionType,
        "venue": venue,
        "topic": topic,
        "topicCategory": this.educationForm.topicCategory,
        "householdId": this.educationForm.householdId,
        "targetAudience": this.educationForm.targetAudience,
        "attendeesCount": this.educationForm.attendeesCount,
        "maleAttendees": this.educationForm.maleAttendees,
        "femaleAttendees": this.educationForm.femaleAttendees,
        "keyMessages": keyMessages,
        "materialsUsed": this.educationForm.materialsUsed.join(', '),
        "demonstrationDone": this.educationForm.demonstrationDone,
        "durationMinutes": this.educationForm.durationMinutes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('recorded');
        } else {
          alert(res.data.message || 'Failed to record education session');
        }
      } catch (e) {
        console.error("Failed to record education session:", e);
        alert('Failed to record education session. Please try again.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  border-color: #556ee6;
}

.form-check.card.border-primary {
  background-color: rgba(85, 110, 230, 0.05) !important;
}

.list-group-item {
  cursor: pointer;
  transition: all 0.2s;
}

.list-group-item:hover {
  background-color: #f8f9fa;
  transform: translateX(5px);
}

.list-group-item h6 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.list-group-item p {
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.input-group {
  position: relative;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
</style>